/**
 * SalesAnalyticsSection 銷售分析 (SR3.3, plan §WR3 — DD!180 Avg Sales & Margin +
 * DD!182 Historical Item Sales, both 0708-J Must).
 *
 * Tier-2 SO/invoice aggregates via the SR1.3 shared selector, served by the
 * SR2.3 `/products/:id/sales-stats` endpoint (OQ-1 default). Because this
 * section and the 訂單毛利報表 report both read the ONE shared selector, the
 * numbers agree by construction (anti-fork — the selector↔endpoint parity is
 * asserted in `salesStatsEndpoint.test.ts`; this surface adds no arithmetic).
 *
 * Data honesty:
 * - Stat tiles (30/90/365d qty, avg monthly qty, avg unit price, avg margin %)
 *   + a chronological month × qty × amount × margin table with an inline qty
 *   Progress bar (NO new chart lib — antd Table + Progress only).
 * - Numeric columns are NEUTRAL (no colour-by-value); the single-series qty bar
 *   is one calm neutral colour scaled to the peak month, never a red/green ramp.
 * - Margin is a FROZEN snapshot; a blank landed cost renders `—`, never 0 (an
 *   unknown margin is not zero profit).
 * - Provenance chip 來源:銷售單 — a Tier-2 aggregate over ERP-owned SO facts
 *   (append-only, staleness-immune), so it carries a source label, not a
 *   NetSuite read-through syncedAt stamp.
 * - LOADING (skeleton) + hard-ERROR (retry) + empty states are defined up front.
 */

import { useMemo } from 'react';
import { Card, Col, Progress, Row, Skeleton, Statistic, Table, Tag, Typography, theme } from 'antd';
import type { TableProps } from 'antd';
import { useTranslation } from 'react-i18next';
import TableContainer, {
  ListEmptyState,
  ListErrorState,
} from '@/components/common/TableContainer';
import { formatAmount } from '@/components/salesOrders/components/soFormat';
import { useProductSalesStats } from '@/hooks/useProducts';
import type { MonthlySalesPoint } from '@/lib/reports/salesHistorySelectors';
import type { Product } from '@/types/api/product';
import SectionHeader from './SectionHeader';

interface SalesAnalyticsSectionProps {
  product: Product;
}

const { Text } = Typography;

/** Tabular-numeric span so every figure column aligns and stays width-bounded. */
const numeric = { fontVariantNumeric: 'tabular-nums' as const };

export const SalesAnalyticsSection = ({ product }: SalesAnalyticsSectionProps) => {
  const { t } = useTranslation(['products', 'common']);
  const { token } = theme.useToken();
  const { stats, error, isLoading, refresh } = useProductSalesStats(product.id);

  const em = t('products:salesAnalytics.marginUnavailable');

  /** Average qty per observed month (Σ month qty ÷ months with history). */
  const avgMonthlyQty = useMemo(() => {
    if (!stats || stats.monthly.length === 0) return 0;
    const total = stats.monthly.reduce((sum, month) => sum + month.qty, 0);
    return total / stats.monthly.length;
  }, [stats]);

  /** Newest month first; peak qty scales the neutral single-series bar. */
  const monthlyRows = useMemo(() => (stats ? [...stats.monthly].reverse() : []), [stats]);
  const peakQty = useMemo(
    () => monthlyRows.reduce((max, month) => (month.qty > max ? month.qty : max), 0),
    [monthlyRows]
  );

  const provenance = <Tag style={{ margin: 0 }}>{t('products:salesAnalytics.source')}</Tag>;

  const header = (
    <SectionHeader
      title={t('products:salesAnalytics.title')}
      subtitle={t('products:salesAnalytics.subtitle')}
      extra={provenance}
    />
  );

  // LOADING — skeleton up front (defined state, never a discovered flash).
  if (isLoading) {
    return (
      <div>
        {header}
        <Skeleton active paragraph={{ rows: 6 }} />
      </div>
    );
  }

  // hard-ERROR — replace the whole body with a retry panel.
  if (error) {
    return (
      <div>
        {header}
        <ListErrorState
          title={t('products:salesAnalytics.states.loadFailed')}
          retryLabel={t('products:salesAnalytics.states.retry')}
          onRetry={refresh}
        />
      </div>
    );
  }

  // Empty — the DEFINED no-history payload (all-zero stats, no months).
  if (!stats || stats.monthly.length === 0) {
    return (
      <div>
        {header}
        <ListEmptyState title={t('products:salesAnalytics.states.empty')} />
      </div>
    );
  }

  const tiles: { key: string; label: string; value: React.ReactNode }[] = [
    { key: 'qtySold30', label: t('products:salesAnalytics.tiles.qtySold30'), value: stats.qtySold30 },
    { key: 'qtySold90', label: t('products:salesAnalytics.tiles.qtySold90'), value: stats.qtySold90 },
    { key: 'qtySold365', label: t('products:salesAnalytics.tiles.qtySold365'), value: stats.qtySold365 },
    { key: 'avgMonthly', label: t('products:salesAnalytics.tiles.avgMonthly'), value: avgMonthlyQty.toFixed(1) },
    {
      key: 'avgUnitPrice',
      label: t('products:salesAnalytics.tiles.avgUnitPrice'),
      value: stats.avgUnitPrice == null ? em : formatAmount(stats.avgUnitPrice),
    },
    {
      key: 'avgMargin',
      label: t('products:salesAnalytics.tiles.avgMargin'),
      value: stats.avgMarginPct == null ? em : `${stats.avgMarginPct.toFixed(1)}%`,
    },
  ];

  const columns: TableProps<MonthlySalesPoint>['columns'] = [
    {
      title: t('products:salesAnalytics.monthly.month'),
      dataIndex: 'month',
      key: 'month',
      width: 120,
      render: (month: string) => <span style={numeric}>{month}</span>,
    },
    {
      title: t('products:salesAnalytics.monthly.qty'),
      dataIndex: 'qty',
      key: 'qty',
      width: 200,
      align: 'right',
      render: (qty: number) => (
        <div className="tw-flex tw-flex-col tw-items-end tw-gap-1">
          <span style={numeric}>{qty}</span>
          <Progress
            percent={peakQty > 0 ? (qty / peakQty) * 100 : 0}
            showInfo={false}
            size="small"
            strokeColor={token.colorTextTertiary}
            trailColor={token.colorFillSecondary}
            style={{ width: 96, margin: 0 }}
          />
        </div>
      ),
    },
    {
      title: t('products:salesAnalytics.monthly.amount'),
      dataIndex: 'amount',
      key: 'amount',
      width: 160,
      align: 'right',
      render: (amount: number) => <span style={numeric}>{formatAmount(amount)}</span>,
    },
    {
      // Frozen per-unit margin snapshot — `—` (never 0) when landed cost is null.
      title: t('products:salesAnalytics.monthly.margin'),
      dataIndex: 'margin',
      key: 'margin',
      width: 140,
      align: 'right',
      render: (margin: number | null) => (
        <span style={numeric}>{margin == null ? em : formatAmount(margin)}</span>
      ),
    },
  ];

  return (
    <div>
      {header}

      <Row gutter={[12, 12]} style={{ marginBottom: token.marginLG }}>
        {tiles.map((tile) => (
          <Col xs={12} md={8} lg={4} key={tile.key}>
            <Card size="small">
              <Statistic
                title={tile.label}
                value={tile.value as string | number}
                valueStyle={numeric}
              />
            </Card>
          </Col>
        ))}
      </Row>

      <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
        {t('products:salesAnalytics.monthly.title')}
      </Text>
      <div style={{ marginTop: token.marginXS }}>
        <TableContainer>
          <Table<MonthlySalesPoint>
            rowKey="month"
            size="small"
            dataSource={monthlyRows}
            columns={columns}
            pagination={monthlyRows.length > 12 ? { pageSize: 12, showSizeChanger: false } : false}
            locale={{
              emptyText: <ListEmptyState title={t('products:salesAnalytics.states.empty')} />,
            }}
            scroll={{ x: 620 }}
          />
        </TableContainer>
      </div>
    </div>
  );
};

export default SalesAnalyticsSection;
